import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Image as ImageIcon,
  Package,
  Pencil,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import type { Product, ProductList } from "@am/shared";
import { useApiClient } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import { PageHeader } from "../components/PageHeader";
import { ConfirmModal } from "../components/ConfirmModal";
import { ProductModal } from "../components/ProductModal";

export function ProductsPage() {
  const api = useApiClient();
  const { t } = useI18n();

  const [products, setProducts] = useState<Product[]>([]);
  const [stats, setStats] = useState<ProductList["stats"] | null>(null);
  const [currency, setCurrency] = useState<string>("EUR");
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<Product | null>(null);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState<Product | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api<ProductList>("/tenant/api/products");
      setProducts(res.products);
      setStats(res.stats);
      setCurrency(res.currency || "EUR");
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("error_generic");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function doDelete() {
    if (!toDelete) return;
    const id = toDelete.id;
    setToDelete(null);
    try {
      await api(`/tenant/api/products/${id}`, { method: "DELETE" });
      toast.success(t("products_delete_ok"));
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("error_generic");
      toast.error(msg);
    }
  }

  function fmtPrice(price: number): string {
    try {
      return price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      return String(price);
    }
  }

  return (
    <div className="page is-active">
      <PageHeader titleKey="products_title" subtitleKey="products_subtitle" />

      {/* Stats bandeau */}
      {stats && (
        <div
          className="stats-row"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <StatCard
            icon={<Package size={16} />}
            label={t("products_stat_total")}
            value={stats.total}
          />
          <StatCard
            icon={<CheckCircle2 size={16} color="var(--green)" />}
            label={t("products_stat_active")}
            value={stats.active}
          />
          <StatCard
            icon={<CheckCircle2 size={16} color="var(--green)" />}
            label={t("products_stat_embedded")}
            value={stats.embedded}
          />
          <StatCard
            icon={<Clock size={16} color="var(--text-muted)" />}
            label={t("products_stat_pending")}
            value={stats.pending}
          />
          <StatCard
            icon={<XCircle size={16} color="var(--red)" />}
            label={t("products_stat_failed")}
            value={stats.failed}
          />
        </div>
      )}

      {/* Header actions */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, gap: 8 }}>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {t("products_currency_label")}: <strong>{currency}</strong>
        </div>
        <button
          type="button"
          className="btn-primary"
          style={{ width: "auto", padding: "8px 16px" }}
          onClick={() => setCreating(true)}
        >
          {t("products_new")}
        </button>
      </div>

      {/* Liste des produits */}
      {loading ? (
        <div style={{ padding: 24, color: "var(--text-muted)" }}>…</div>
      ) : products.length === 0 ? (
        <div
          className="card"
          style={{
            padding: 32,
            textAlign: "center",
            color: "var(--text-muted)",
          }}
        >
          <Package size={32} style={{ opacity: 0.5, marginBottom: 8 }} />
          <div>{t("products_empty")}</div>
        </div>
      ) : (
        <div
          className="products-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 12,
          }}
        >
          {products.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              currency={currency}
              fmtPrice={fmtPrice}
              onEdit={() => setEditing(p)}
              onDelete={() => setToDelete(p)}
            />
          ))}
        </div>
      )}

      {(creating || editing) && (
        <ProductModal
          product={editing}
          currency={currency}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await reload();
          }}
        />
      )}

      <ConfirmModal
        open={toDelete !== null}
        title={t("products_delete_confirm_title")}
        body={toDelete?.name}
        confirmLabel={t("products_delete")}
        danger
        onConfirm={doDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
}

function StatCard({ icon, label, value }: StatCardProps) {
  return (
    <div
      className="card"
      style={{
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-muted)", fontSize: 12 }}>
        {icon}
        <span>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

interface ProductCardProps {
  product: Product;
  currency: string;
  fmtPrice: (n: number) => string;
  onEdit: () => void;
  onDelete: () => void;
}

function ProductCard({ product, currency, fmtPrice, onEdit, onDelete }: ProductCardProps) {
  const { t } = useI18n();
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className="card"
      style={{
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        opacity: product.is_active ? 1 : 0.6,
      }}
    >
      {/* Image */}
      <div
        style={{
          width: "100%",
          aspectRatio: "16/10",
          borderRadius: 6,
          background: "var(--bg-elevated)",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid var(--border)",
        }}
      >
        {product.image_url && !imgError ? (
          <img
            src={product.image_url}
            alt={product.name}
            onError={() => setImgError(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <ImageIcon size={28} color="var(--text-muted)" />
        )}
      </div>

      {/* Titre + prix */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, wordBreak: "break-word" }}>
          {product.name}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--purple)" }}>
          {fmtPrice(product.price)} {currency}
        </div>
      </div>

      {/* Status embed + inactif */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <EmbedBadge status={product.embed_status} error={product.embed_error} />
        {!product.is_active && (
          <span
            className="badge"
            style={{
              background: "var(--bg-elevated)",
              color: "var(--text-muted)",
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid var(--border)",
            }}
          >
            {t("products_inactive_badge")}
          </span>
        )}
        {product.variants.length > 0 && (
          <span
            className="badge"
            style={{
              background: "var(--bg-elevated)",
              color: "var(--text-muted)",
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid var(--border)",
            }}
          >
            {product.variants.length} {product.variants.length > 1 ? t("products_variants_plural") : t("products_variants_singular")}
          </span>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: "auto",
          paddingTop: 4,
          borderTop: "1px solid var(--border)",
        }}
      >
        <button
          type="button"
          className="btn-sm btn-ghost"
          style={{ flex: 1, justifyContent: "center" }}
          onClick={onEdit}
        >
          <Pencil size={13} /> {t("products_edit")}
        </button>
        <button
          type="button"
          className="btn-sm btn-ghost"
          title={t("products_delete")}
          onClick={onDelete}
          style={{ color: "var(--red)" }}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

interface EmbedBadgeProps {
  status: string;
  error: string | null;
}

function EmbedBadge({ status, error }: EmbedBadgeProps) {
  const { t } = useI18n();
  const pending = {
    icon: <Clock size={10} />,
    label: t("products_status_pending"),
    color: "var(--text-muted)",
    bg: "var(--bg-elevated)",
  };
  const config: Record<string, { icon: React.ReactNode; label: string; color: string; bg: string }> = {
    embedded: {
      icon: <CheckCircle2 size={10} />,
      label: t("products_status_embedded"),
      color: "var(--green)",
      bg: "color-mix(in srgb, var(--green) 12%, transparent)",
    },
    pending,
    outdated: {
      icon: <AlertTriangle size={10} />,
      label: t("products_status_outdated"),
      color: "var(--yellow)",
      bg: "color-mix(in srgb, var(--yellow) 12%, transparent)",
    },
    failed: {
      icon: <XCircle size={10} />,
      label: t("products_status_failed"),
      color: "var(--red)",
      bg: "color-mix(in srgb, var(--red) 12%, transparent)",
    },
  };
  const cfg = config[status] ?? pending;
  return (
    <span
      className="badge"
      title={error ?? undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: cfg.bg,
        color: cfg.color,
        fontSize: 10,
        padding: "2px 6px",
        borderRadius: 4,
        border: `1px solid ${cfg.color}`,
      }}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}
